import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { ThemeConfig } from './types'
import { defaultThemeConfig } from './defaults'
import { themeApi } from './themeApi'

interface ThemeConfigContextValue {
  config: ThemeConfig
  isLoading: boolean
  error: string | null
  updateConfig: (config: ThemeConfig) => Promise<void>
  resetConfig: () => Promise<void>
}

const ThemeConfigContext = createContext<ThemeConfigContextValue | null>(null)

interface ThemeConfigProviderProps {
  children: ReactNode
  config: ThemeConfig
  onConfigChange: (config: ThemeConfig) => void
}

export function ThemeConfigProvider({ children, config, onConfigChange }: ThemeConfigProviderProps) {
  const [error, setError] = useState<string | null>(null)

  const updateConfig = useCallback(async (newConfig: ThemeConfig) => {
    try {
      await themeApi.saveTheme(newConfig)
      onConfigChange(newConfig)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save theme'
      setError(message)
      throw new Error(message)
    }
  }, [onConfigChange])

  const resetConfig = useCallback(async () => {
    try {
      await themeApi.resetTheme()
      onConfigChange(defaultThemeConfig)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset theme'
      setError(message)
      throw new Error(message)
    }
  }, [onConfigChange])

  return (
    <ThemeConfigContext.Provider
      value={{ config, isLoading: false, error, updateConfig, resetConfig }}
    >
      {children}
    </ThemeConfigContext.Provider>
  )
}

export function useThemeConfig() {
  const context = useContext(ThemeConfigContext)
  if (!context) {
    throw new Error('useThemeConfig must be used within a ThemeConfigProvider')
  }
  return context
}
